<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-10-09 (0.1.0) (jhoward)
 *   - Add GuestHelpWidget.vue, the guest FAQ bot (stage 2): a help button on every page that answers from published
 *     FAQ articles, links login problems to the self-service pages, and hands off to the contact form. *
 * 2026-10-09 (0.2.0) (jhoward)
 *   - Stage 3: a guest signed in past two-factor asks the account bot instead, which answers with cards built from
 *     their own account (points and tier, offers, hotel offers, win/loss years) and passes anything else to the FAQ
 *     bot. Re-checks which bot to use when the guest signs in or out, since the widget outlives both.
 *
 * 2026-10-10 (0.3.0) (jhoward)
 *   - Stage 4: offer action cards. Their button does exactly what the same button on the Offers page does - Redeem and
 *     Cancel open the shared OfferManagementDialog, Book Room runs validateHotelOffer() - with the offer loaded the way
 *     that page loads it. The widget never calls a redeem, cancel or booking endpoint itself; the guest confirms (and
 *     reauthenticates) in the existing flow.
 *   - Each offer's title links to that offer's own page (/offers/{domain}/{code}/view), so a guest can open the one
 *     the answer is about rather than only "See all my offers".
 *
 * 2026-10-10 (0.4.0) (jhoward)
 *   - The question box stays at the bottom under each answer, so a guest types the next question straight in; the
 *     "Ask another question" link is gone.
 *   - The hand-off opens the Contact page with the guest's question already in the comment (passed in history state,
 *     never the URL) and shows the phone number, in place of the contact form squeezed into the panel.
 *   - The widget's own calls (status, ask, feedback) no longer raise the page-wide loading overlay; a question in
 *     progress shows a PeppermillLoadingBar along the top of the question form instead. An offer action still
 *     raises the overlay while it loads the offer, since it hands over to the Offers page's own flow.
 *
 * 2026-10-10 (0.5.0) (jhoward)
 *   - Render the account bot's tournament_results card: name, day, property, rank, score and prize, newest first.
 *   - Suggested questions as chips when the panel opens, one per kind of answer (account cards for a signed-in guest;
 *     login help, FAQ and talking to a person otherwise). A chip asks its question as if typed.
 *   - GA4 events through trackChatbotEvent(): open and close, suggestion clicks, questions (length only, never the
 *     text), answers (kind, cards, response time), errors, feedback, FAQ articles opened, links and offer actions
 *     followed, and hand-offs to the contact page or phone.
 */

import MaterialButton from '@framework/components/material/MaterialButton.vue';
import PeppermillBadge from '@framework/components/PeppermillBadge.vue';
import PeppermillChip from '@framework/components/PeppermillChip.vue';
import PeppermillLoadingBar from '@framework/components/PeppermillLoadingBar.vue';
import type { FetchResponse } from '@framework/defs/framework';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import getAccountOffers from '@/helpers/getAccountOffers';
import getPropertyName from '@/helpers/getPropertyName';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import trackChatbotEvent from '@/helpers/trackChatbotEvent';
import type { ChatbotCloseReason, ChatbotLinkType, ChatbotName } from '@/helpers/trackChatbotEvent';
import validateHotelOffer from '@/helpers/validateHotelOffer';
import useAppStore from '@/store';
import { ask, feedback, status } from '@rss/routes/guest-help';
import { ask as askAccount } from '@rss/routes/guest-help/account';

type HelpArticle = {
  id: number;
  question: string;
  /** Employee-written, rendered and sanitised on the server (FaqArticle::answerHtml()). */
  answer_html: string;
};

type HelpLink = { label: string; path: string };

type HelpOffer = {
  code: string;
  /** With the code, the offer's own page (offers.view). */
  domain?: string;
  title: string;
  subtitle?: string;
  begins?: string | null;
  ends?: string | null;
  is_hotel_offer?: boolean;
  hotel_arrival_date?: string | null;
  hotel_nights?: number | null;
  call_hotel_to_book?: 'YES' | 'NO' | '' | null;
};

type HelpTournamentResult = {
  name: string;
  /** YYYY-MM-DD. */
  date: string | null;
  property_code: string | null;
  rank: number | null;
  prize: string | null;
  score: number;
  is_linked_account: boolean;
};

/** Built by GuestAccountService from the guest's own account data - never model output. */
type HelpCard =
  | {
      type: 'account_summary';
      tier?: string;
      next_tier?: string;
      points?: number;
      comps?: number;
      points_needed_to_keep_tier?: number;
      points_needed_for_next_tier?: number;
      link: HelpLink;
    }
  | { type: 'offers' | 'hotel_offers'; count: number; offers: HelpOffer[]; link: HelpLink }
  | { type: 'win_loss_years'; years: number[]; link: HelpLink }
  | { type: 'tournament_results'; count: number; results: HelpTournamentResult[]; link: HelpLink }
  | {
      type: 'offer_action';
      action: 'redeem' | 'cancel' | 'book';
      /** Checked on the server by the Offers page's rules; when false, `reason` says why and there is no button. */
      allowed: boolean;
      reason: string | null;
      button_label: string;
      offer: HelpOffer & { domain: string };
      link: HelpLink;
    };

type HelpAnswer = {
  answer_id: string | null;
  kind: 'faq' | 'login_help' | 'responsible_gaming' | 'handoff' | 'no_match' | 'account';
  /** Fixed copy from GuestHelpService or GuestAccountService - never model output. */
  message: string;
  articles: HelpArticle[];
  link: HelpLink | null;
  handoff: boolean;
  /** Fixed copy, or the account bot's one sentence after the server has checked its numbers against the cards. */
  note: string | null;
  cards: HelpCard[];
  removed_from_message: string[];
};

type StatusResponse = FetchResponse & {
  data?: { enabled: boolean; account_enabled: boolean };
};

interface AskResponse extends FetchResponse {
  data?: HelpAnswer;
}

/** Must match App\Http\Controllers\GuestHelpController::HONEYPOT_FIELD. */
const HONEYPOT_FIELD = 'website';

/** The number the Contact, Home and Offers pages give. */
const SUPPORT_PHONE = '800-217-0049';

const appStore = useAppStore();
const router = useRouter();

const isEnabled = ref<boolean>(false);
const isAccountEnabled = ref<boolean>(false);
const isOpen = ref<boolean>(false);
const question = ref<string>('');
const askedQuestion = ref<string>('');
const honeypot = ref<string>('');
const isAsking = ref<boolean>(false);
const answer = ref<HelpAnswer | null>(null);
const openArticleId = ref<number | null>(null);
const vote = ref<'yes' | 'no' | null>(null);
const input = ref<HTMLInputElement | null>(null);

/** Which bot answers, for analytics. */
const bot = computed<ChatbotName>(() => (isAccountEnabled.value ? 'account' : 'faq'));
/** Questions answered since the panel last opened. */
const questionsAsked = ref<number>(0);

async function open(): Promise<void> {
  isOpen.value = true;
  questionsAsked.value = 0;
  trackChatbotEvent('chatbot_open', { bot: bot.value });
  await nextTick();
  input.value?.focus();
}

/** Closes the panel: from its close button, or because the guest followed a link out of it. */
function close(reason: ChatbotCloseReason): void {
  if (!isOpen.value) {
    return;
  }
  isOpen.value = false;
  trackChatbotEvent('chatbot_close', { bot: bot.value, questions_asked: questionsAsked.value, close_reason: reason });
}

function followLink(linkType: ChatbotLinkType, path: string): void {
  trackChatbotEvent('chatbot_link_click', { bot: bot.value, link_type: linkType, link_path: path });
  close('link');
}

function toggleArticle(articleId: number): void {
  const opening = openArticleId.value !== articleId;
  openArticleId.value = opening ? articleId : null;
  if (opening) {
    trackChatbotEvent('chatbot_article_open', { bot: bot.value, article_id: articleId });
  }
}

function callUs(): void {
  trackChatbotEvent('chatbot_handoff', { bot: bot.value, method: 'phone' });
}

type Suggestion = { question: string; icon: string };

/**
 * Shown when the panel opens, one per kind of answer the bot gives, so a guest sees what it can do. Asked exactly as
 * if typed.
 */
const ACCOUNT_SUGGESTIONS: Suggestion[] = [
  { question: 'How many points do I have?', icon: 'stars' },
  { question: 'What offers do I have?', icon: 'local_offer' },
  { question: 'Do I have any hotel offers?', icon: 'hotel' },
  { question: 'Show my tournament results', icon: 'emoji_events' },
  { question: 'Which win/loss statements can I print?', icon: 'receipt_long' },
  { question: 'How do I find my casino host?', icon: 'support_agent' },
];

const GUEST_SUGGESTIONS: Suggestion[] = [
  { question: 'I forgot my password', icon: 'lock_reset' },
  { question: 'I forgot my username', icon: 'person_search' },
  { question: 'How do I join the Passport Club?', icon: 'card_membership' },
  { question: 'How do I find my casino host?', icon: 'support_agent' },
  { question: 'I want to talk to a person', icon: 'forum' },
];

const suggestions = computed<Suggestion[]>(() => (isAccountEnabled.value ? ACCOUNT_SUGGESTIONS : GUEST_SUGGESTIONS));

async function askSuggestion(text: string): Promise<void> {
  if (isAsking.value) {
    return;
  }
  trackChatbotEvent('chatbot_suggestion_click', { bot: bot.value, suggestion: text });
  question.value = text;
  await submit('suggestion');
}

async function submit(source: 'typed' | 'suggestion' = 'typed'): Promise<void> {
  const text = question.value.trim();
  if (text.length < 3 || isAsking.value) {
    return;
  }

  isAsking.value = true;
  answer.value = null;
  vote.value = null;

  //The length only: what a guest types can hold their name, account number or worse, and never goes to GA.
  trackChatbotEvent('chatbot_question', { bot: bot.value, source, question_length: text.length });
  const startedAt = performance.now();

  const response = await peppermillCustomFetchProxy<AskResponse>(
    { question: text, [HONEYPOT_FIELD]: honeypot.value },
    { route: isAccountEnabled.value ? askAccount() : ask(), showLoadingSpinner: false },
  );

  if (200 === response.status && response.data) {
    questionsAsked.value++;
    trackChatbotEvent('chatbot_answer', {
      bot: bot.value,
      kind: response.data.kind,
      cards: response.data.cards.map((card) => card.type).join(',') || '(none)',
      article_count: response.data.articles.length,
      has_note: null !== response.data.note,
      handoff: response.data.handoff,
      pii_removed: response.data.removed_from_message.length > 0,
      response_ms: Math.round(performance.now() - startedAt),
    });

    answer.value = response.data;
    askedQuestion.value = text;
    question.value = '';
    //One answer shown open, so the guest reads it without an extra click.
    openArticleId.value = response.data.articles[0]?.id ?? null;

    if (response.data.removed_from_message.length) {
      appStore.setNewSnack(
        10000,
        "We removed a card number, PIN or similar from your question. Please don't share these here.",
        'privacy_tip',
      );
    }
  } else {
    trackChatbotEvent('chatbot_error', { bot: bot.value, status: response.status });
    appStore.setNewSnack(
      10000,
      response.message ?? 'Help is not available right now. Please try the FAQ page.',
      'error',
    );
  }

  isAsking.value = false;
}

/**
 * The Contact page, with the question the guest asked already in the comment. It goes in history state, not the
 * query string, so it stays out of server logs and browser history.
 */
async function openContactPage(): Promise<void> {
  trackChatbotEvent('chatbot_handoff', { bot: bot.value, method: 'contact_page' });
  close('link');
  await router.push({ name: 'contact', state: { helpQuestion: askedQuestion.value } });
}

/** The offer's own page, the one "View details" opens from the Offers page. */
function offerPath(offer: HelpOffer): string {
  return `/offers/${encodeURIComponent(offer.domain ?? '')}/${encodeURIComponent(offer.code)}/view`;
}

const isStartingAction = ref<boolean>(false);

/**
 * What the same button on the Offers page does, with the offer loaded the way that page loads it: Book Room starts
 * validateHotelOffer(), Redeem and Cancel open the shared OfferManagementDialog (which itself decides between the two
 * from the offer, and confirms before calling anything).
 */
async function startOfferAction(card: Extract<HelpCard, { type: 'offer_action' }>): Promise<void> {
  if (isStartingAction.value) {
    return;
  }

  isStartingAction.value = true;
  trackChatbotEvent('chatbot_offer_action', {
    bot: bot.value,
    action: card.action,
    offer_code: card.offer.code,
    offer_domain: card.offer.domain,
  });
  try {
    const offers = await getAccountOffers(false);
    const offer = offers.find(
      (item) => item.CASINO_OFFER_CODE === card.offer.code && item.CASINO_OFFER_DOMAIN === card.offer.domain,
    );

    if (!offer) {
      appStore.setNewSnack(10000, 'That offer is no longer on your account. Your Offers page has the latest.', 'error');
    } else if ('book' === card.action) {
      await validateHotelOffer(offer);
    } else {
      appStore.offerManagementDialog.offer = offer;
    }
  } finally {
    isStartingAction.value = false;
  }
}

async function sendFeedback(helpful: boolean): Promise<void> {
  if (!answer.value?.answer_id || vote.value) {
    return;
  }
  vote.value = helpful ? 'yes' : 'no';
  trackChatbotEvent('chatbot_feedback', { bot: bot.value, kind: answer.value.kind, helpful: vote.value });
  await peppermillCustomFetchProxy(
    { answer_id: answer.value.answer_id, helpful: helpful ? 1 : 0 },
    { route: feedback(), showLoadingSpinner: false },
  );
}

const numberFormat = new Intl.NumberFormat('en-US');
const currencyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

function formatDate(date: string | null | undefined): string {
  //Dates arrive as YYYY-MM-DD; parsed as UTC midnight they would show the day before west of Greenwich.
  return date ? dateFormat.format(new Date(`${date}T12:00:00`)) : '';
}

const signedInKey = computed(
  () => `${appStore.state.user?.account_id ?? ''}|${appStore.state.twoFactorAuthRequired ? 'partial' : 'full'}`,
);

async function refreshStatus(): Promise<void> {
  const response = await peppermillCustomFetchProxy<StatusResponse>({}, { route: status(), showLoadingSpinner: false });
  isEnabled.value = 200 === response.status && true === response.data?.enabled;
  isAccountEnabled.value = 200 === response.status && true === response.data?.account_enabled;
}

onMounted(refreshStatus);

//Signing in, finishing two-factor or signing out changes which bot answers (and whether one does at all).
watch(signedInKey, () => {
  answer.value = null;
  void refreshStatus();
});
</script>

<template>
  <div v-if="isEnabled" class="guest_help">
    <button v-if="!isOpen" type="button" class="guest_help--launcher" aria-haspopup="dialog" @click="open()">
      <span class="material-icons" aria-hidden="true">help</span>
      Help
    </button>

    <section v-else class="guest_help--panel" role="dialog" aria-label="Help" aria-modal="false">
      <header class="guest_help--header">
        <strong>How can we help?</strong>
        <button type="button" class="guest_help--close" aria-label="Close help" @click="close('close_button')">
          <span class="material-icons" aria-hidden="true">close</span>
        </button>
      </header>

      <div class="guest_help--body" aria-live="polite">
        <p v-if="!answer && !isAsking" class="guest_help--intro">
          <template v-if="isAccountEnabled">
            Ask about your points, tier, offers, tournament results or win/loss statements, or anything in our FAQ.
          </template>
          <template v-else>Ask a question. Answers come from our FAQ, written by our team.</template>
          <strong>Never share your PIN, password or card number here.</strong>
        </p>

        <div v-if="!answer && !isAsking" class="guest_help--suggestions" aria-label="Suggested questions">
          <peppermill-chip
            v-for="suggestion in suggestions"
            :key="suggestion.question"
            :label="suggestion.question"
            :icon="suggestion.icon"
            is-selectable
            @update:selected="askSuggestion(suggestion.question)"
          ></peppermill-chip>
        </div>

        <p v-if="isAsking" class="guest_help--muted">Looking that up…</p>

        <template v-if="answer">
          <p class="guest_help--asked">"{{ askedQuestion }}"</p>
          <p>{{ answer.message }}</p>

          <div v-for="article in answer.articles" :key="article.id" class="guest_help--article">
            <button
              type="button"
              class="guest_help--article_question"
              :aria-expanded="openArticleId === article.id"
              @click="toggleArticle(article.id)"
            >
              {{ article.question }}
            </button>
            <!-- Employee-written FAQ text, sanitised on the server. -->
            <div
              v-show="openArticleId === article.id"
              class="guest_help--article_answer"
              v-html="article.answer_html"
            ></div>
          </div>

          <!-- Account cards: every figure here is from the guest's own account, never from the model. -->
          <div v-for="card in answer.cards" :key="card.type" class="guest_help--card">
            <dl v-if="'account_summary' === card.type" class="guest_help--figures">
              <template v-if="card.tier">
                <dt>Tier</dt>
                <dd>{{ card.tier }}</dd>
              </template>
              <template v-if="undefined !== card.points">
                <dt>Tier points</dt>
                <dd>{{ numberFormat.format(card.points) }}</dd>
              </template>
              <template v-if="undefined !== card.comps">
                <dt>Comps</dt>
                <dd>{{ currencyFormat.format(card.comps) }}</dd>
              </template>
              <template v-if="card.points_needed_to_keep_tier">
                <dt>To keep {{ card.tier }}</dt>
                <dd>{{ numberFormat.format(card.points_needed_to_keep_tier) }} more points</dd>
              </template>
              <template v-if="card.next_tier && card.points_needed_for_next_tier">
                <dt>To reach {{ card.next_tier }}</dt>
                <dd>{{ numberFormat.format(card.points_needed_for_next_tier) }} more points</dd>
              </template>
            </dl>

            <template v-else-if="'offers' === card.type || 'hotel_offers' === card.type">
              <strong>{{ 'hotel_offers' === card.type ? 'Your hotel offers' : 'Your offers' }}</strong>
              <p v-if="0 === card.count" class="guest_help--muted">
                {{
                  'hotel_offers' === card.type ? 'You have no hotel offers right now.' : 'You have no offers right now.'
                }}
              </p>
              <ul v-else class="guest_help--offers">
                <li v-for="offer in card.offers" :key="offer.code">
                  <router-link
                    v-if="offer.domain"
                    :to="offerPath(offer)"
                    class="guest_help--offer_title guest_help--offer_link"
                    @click="followLink('offer_link', offerPath(offer))"
                  >
                    {{ offer.title }}
                    <span class="material-icons" aria-hidden="true">chevron_right</span>
                  </router-link>
                  <span v-else class="guest_help--offer_title">{{ offer.title }}</span>
                  <span v-if="offer.hotel_nights" class="guest_help--muted">
                    {{ offer.hotel_nights }} {{ 1 === offer.hotel_nights ? 'night' : 'nights' }}
                    <template v-if="offer.hotel_arrival_date"
                      >, arriving {{ formatDate(offer.hotel_arrival_date) }}</template
                    >
                  </span>
                  <span v-if="offer.ends" class="guest_help--muted">Ends {{ formatDate(offer.ends) }}</span>
                  <span v-if="'YES' === offer.call_hotel_to_book" class="guest_help--muted"
                    >Call the hotel to book</span
                  >
                </li>
              </ul>
              <p v-if="card.count > card.offers.length" class="guest_help--muted">
                And {{ card.count - card.offers.length }} more.
              </p>
            </template>

            <template v-else-if="'win_loss_years' === card.type">
              <strong>Win/loss statements</strong>
              <p v-if="card.years.length">You can print a statement for {{ card.years.join(', ') }}.</p>
              <p v-else class="guest_help--muted">No win/loss statements are available yet.</p>
            </template>

            <template v-else-if="'tournament_results' === card.type">
              <strong>Your tournament results</strong>
              <p v-if="0 === card.count" class="guest_help--muted">
                We don't have any tournament results for your account.
              </p>
              <ul v-else class="guest_help--offers">
                <li v-for="(result, index) in card.results" :key="`${result.date}::${result.name}::${index}`">
                  <span class="guest_help--offer_title">{{ result.name }}</span>
                  <span class="guest_help--muted">
                    {{ formatDate(result.date) }}, {{ getPropertyName(result.property_code) }}
                    <template v-if="result.is_linked_account"> (linked account)</template>
                  </span>
                  <span>
                    <template v-if="result.rank">Rank {{ numberFormat.format(result.rank) }} · </template>
                    Score {{ numberFormat.format(result.score) }}
                    <template v-if="result.prize"> · Prize {{ result.prize }}</template>
                  </span>
                </li>
              </ul>
              <p v-if="card.count > card.results.length" class="guest_help--muted">
                And {{ card.count - card.results.length }} more.
              </p>
            </template>

            <template v-else-if="'offer_action' === card.type">
              <router-link
                :to="offerPath(card.offer)"
                class="guest_help--offer_title guest_help--offer_link"
                @click="followLink('offer_link', offerPath(card.offer))"
              >
                {{ card.offer.title }}
                <span class="material-icons" aria-hidden="true">chevron_right</span>
              </router-link>
              <span v-if="card.offer.subtitle" class="guest_help--muted">{{ card.offer.subtitle }}</span>
              <span v-if="card.offer.hotel_nights" class="guest_help--muted">
                {{ card.offer.hotel_nights }} {{ 1 === card.offer.hotel_nights ? 'night' : 'nights' }}
                <template v-if="card.offer.hotel_arrival_date">
                  , arriving {{ formatDate(card.offer.hotel_arrival_date) }}
                </template>
              </span>
              <span v-if="card.offer.ends" class="guest_help--muted">Ends {{ formatDate(card.offer.ends) }}</span>
              <div class="guest_help--action">
                <material-button
                  v-if="card.allowed"
                  :label="card.button_label"
                  type="button"
                  mod="raised"
                  :disabled="isStartingAction"
                  @click="startOfferAction(card)"
                ></material-button>
                <peppermill-badge v-else :label="card.reason ?? ''" variant="warning" icon="info"></peppermill-badge>
              </div>
              <p v-if="card.allowed" class="guest_help--muted">
                You'll confirm {{ 'book' === card.action ? 'your booking' : 'this' }} before anything changes.
              </p>
            </template>

            <router-link
              :to="card.link.path"
              class="guest_help--card_link"
              @click="followLink('card_link', card.link.path)"
            >
              {{ card.link.label }}
            </router-link>
          </div>

          <p v-if="answer.note" class="guest_help--note">{{ answer.note }}</p>

          <material-button
            v-if="answer.link"
            :label="answer.link.label"
            type="router-link"
            :href="answer.link.path"
            mod="raised"
            @click="followLink('answer_link', answer.link.path)"
          ></material-button>

          <div v-if="answer.handoff" class="guest_help--handoff">
            <material-button
              label="Message our team"
              type="button"
              mod="raised"
              @click="openContactPage()"
            ></material-button>
            <p>
              Or call us at <a :href="`tel:${SUPPORT_PHONE}`" @click="callUs()">{{ SUPPORT_PHONE }}</a
              >.
            </p>
          </div>

          <p v-if="answer.answer_id && 'responsible_gaming' !== answer.kind" class="guest_help--feedback">
            <template v-if="!vote">
              Was this helpful?
              <button type="button" @click="sendFeedback(true)">Yes</button>
              <button type="button" @click="sendFeedback(false)">No</button>
            </template>
            <template v-else>Thanks for letting us know.</template>
          </p>
        </template>
      </div>

      <!-- Always at the bottom, so the next question is typed straight in under the last answer. -->
      <form class="guest_help--form" @submit.prevent="submit()">
        <!-- The panel's own loader, in place of the page-wide overlay. -->
        <peppermill-loading-bar :is-active="isAsking"></peppermill-loading-bar>
        <label for="guest-help-question" class="guest_help--label">{{
          answer ? 'Ask another question' : 'Your question'
        }}</label>
        <input
          id="guest-help-question"
          ref="input"
          v-model="question"
          type="text"
          maxlength="300"
          autocomplete="off"
          placeholder="For example: I forgot my password"
          :disabled="isAsking"
        />
        <!-- Honeypot: hidden from people, filled in by bots. -->
        <input
          v-model="honeypot"
          class="guest_help--honeypot"
          :name="HONEYPOT_FIELD"
          type="text"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
        />
        <material-button label="Ask" type="submit" :disabled="isAsking || question.trim().length < 3"></material-button>
      </form>
    </section>
  </div>
</template>

<style scoped>
/* z-index 34: see the register at the top of src/main.css. */
.guest_help {
  bottom: 1em;
  position: fixed;
  right: 1em;
  z-index: 34;
}

.guest_help--launcher {
  align-items: center;
  background: rgb(var(--primary-color));
  border: 0;
  border-radius: 2em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  color: #fff;
  cursor: pointer;
  display: flex;
  font: inherit;
  font-weight: 600;
  gap: 0.4em;
  padding: 0.7em 1.1em;
}

.guest_help--panel {
  background: rgb(var(--background-color, 255, 255, 255));
  border-radius: 0.75em;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: min(70vh, 36em);
  width: min(24em, calc(100vw - 2em));
}

.guest_help--header {
  align-items: center;
  background: rgb(var(--primary-color));
  border-radius: 0.75em 0.75em 0 0;
  color: #fff;
  display: flex;
  justify-content: space-between;
  padding: 0.6em 0.8em;
}

.guest_help--close {
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
}

/* The answer scrolls; the question form under it stays in view. */
.guest_help--body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.8em;
}

.guest_help--intro,
.guest_help--muted,
.guest_help--asked {
  margin-top: 0;
}

.guest_help--muted,
.guest_help--asked {
  opacity: 0.7;
}

.guest_help--article {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.guest_help--article_question {
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0.6em 0;
  text-align: left;
  width: 100%;
}

.guest_help--article_answer {
  padding-bottom: 0.6em;
}

.guest_help--article_answer :deep(p) {
  margin: 0 0 0.5em;
}

.guest_help--note {
  font-weight: 600;
}

.guest_help--card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.5em;
  margin-bottom: 0.75em;
  padding: 0.6em 0.8em;
}

.guest_help--figures {
  display: grid;
  gap: 0.25em 1em;
  grid-template-columns: auto 1fr;
  margin: 0 0 0.5em;
}

.guest_help--figures dt {
  opacity: 0.7;
}

.guest_help--figures dd {
  font-weight: 600;
  margin: 0;
  text-align: right;
}

.guest_help--offers {
  list-style: none;
  margin: 0.5em 0;
  padding: 0;
}

.guest_help--offers li {
  display: flex;
  flex-direction: column;
  padding: 0.3em 0;
}

.guest_help--offers li + li {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.guest_help--offer_title {
  font-weight: 600;
}

.guest_help--offer_link {
  align-items: center;
  color: rgb(var(--primary-color));
  display: inline-flex;
  text-decoration: none;
}

.guest_help--offer_link:hover {
  text-decoration: underline;
}

.guest_help--offer_link .material-icons {
  font-size: 18px;
}

/* The offer action card's lines. */
.guest_help--card > span {
  display: block;
}

.guest_help--action {
  margin: 0.6em 0 0.3em;
}

.guest_help--card_link {
  color: rgb(var(--primary-color));
  font-weight: 600;
}

.guest_help--suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
}

.guest_help--handoff p {
  margin: 0.5em 0 0;
}

.guest_help--handoff a {
  color: rgb(var(--primary-color));
  font-weight: 600;
}

.guest_help--feedback button {
  background: none;
  border: 0;
  color: rgb(var(--primary-color));
  cursor: pointer;
  font: inherit;
  text-decoration: underline;
}

.guest_help--form {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 0.5em;
  padding: 0.8em;
  /* Holds the loading bar, which sits along the form's top edge. */
  position: relative;
}

.guest_help--form input {
  font: inherit;
  padding: 0.5em;
}

.guest_help--label {
  font-weight: 600;
}

.guest_help--honeypot {
  height: 1px;
  left: -10000px;
  opacity: 0;
  position: absolute;
  width: 1px;
}
</style>
